import type {
	EventReanalysisView,
	ReanalysisSuggestion,
} from "@sitescene/types";

import { deriveReanalysisKind } from "@sitescene/shared";
import { NONE_CLASS_ID } from "@sitescene/types";
import { useState } from "react";

import { DetailPanel } from "@/components/detail-panel";
import { Button, Field, Select, TextArea } from "@/components/field";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";
import {
	describeReanalysis,
	NOTHING_ALERTABLE,
	resolveTarget,
} from "@/features/reanalysis/describe-reanalysis";
import {
	isReanalysisOpen,
	useRequestReanalysis,
	useSuggestReanalysisClass,
} from "@/features/reanalysis/use-reanalysis";
import { apiErrorMessage } from "@/features/scene-facts/use-scene-proposals";

interface Suggestion {
	/** The words it was read from, so the same words are not read twice. */
	from: string;
	result: ReanalysisSuggestion;
}

/**
 * Staff's correction of the event: what the clip really showed, in words and
 * as a class, and what the event got wrong. Which correction that makes is
 * derived, never picked, and said in plain words before Submit, so staff
 * know what the footage will be tested for.
 */
export const CorrectionPanel = ({
	eventId,
	view,
}: {
	eventId: string;
	view: EventReanalysisView;
}) => {
	const taxonomy = useTaxonomy();
	const suggest = useSuggestReanalysisClass(eventId);
	const request = useRequestReanalysis(eventId);
	const [whatHappened, setWhatHappened] = useState("");
	const [targetClassId, setTargetClassId] = useState("");
	const [whatWasWrong, setWhatWasWrong] = useState("");
	const [suggestion, setSuggestion] = useState<null | Suggestion>(null);

	const { context } = view;
	const classes = taxonomy.data ?? [];
	const target = resolveTarget(targetClassId, classes);
	const kind = target
		? deriveReanalysisKind({
				currentClassId: context.currentClassId,
				eventSeverity: context.eventSeverity,
				target,
			})
		: null;
	const text = whatHappened.trim();
	// Only sent with the request while staff kept it; changing the class means
	// they overrode it, and recording it as accepted would overstate the
	// suggester.
	const kept =
		suggestion &&
		suggestion.result.method !== "none" &&
		suggestion.result.classId === targetClassId
			? suggestion.result
			: null;
	const blocker = !context.hasClip
		? "No detection on this event has a clip, so there is nothing to reanalyze."
		: view.reanalyses.some(isReanalysisOpen)
			? "A reanalysis of this event is still running. Wait for it to finish before starting another."
			: undefined;
	const canSubmit =
		!blocker && kind !== null && Boolean(text) && !request.isPending;

	const readWords = () => {
		suggest.mutate(text, {
			// A failed call answered nothing, so drop the previous suggestion
			// rather than leaving it to read as a reply to the new words.
			onError: () => setSuggestion(null),
			onSuccess: (result) => {
				setSuggestion({ from: text, result });
				// A `none` method means the words matched nothing, not that nothing
				// alertable happened, so the class stays staff's to choose.
				if (result.method !== "none") setTargetClassId(result.classId);
			},
		});
	};

	// Leaving the box reads the words only when they changed since the last
	// reading, and never over a class staff chose themselves.
	const readWordsOnBlur = () => {
		if (!text || suggest.isPending || suggestion?.from === text) return;
		if (targetClassId && !kept) return;
		readWords();
	};

	return (
		<DetailPanel title="Correct this event">
			<p className="text-secondary mb-3 text-sm">
				The event reads as{" "}
				<span className="text-primary font-medium">
					{context.currentClassName ??
						context.currentClassId ??
						"a class the verifier could not place"}
				</span>{" "}
				at severity {context.eventSeverity}. Say what the clip really shows: the
				verifier re-runs anchored on your answer, and if the footage supports it
				the event's severity, title and description update on their own.
			</p>
			<form
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					if (!canSubmit) return;
					request.mutate(
						{
							suggestedClassId: kept?.classId,
							suggestionMethod: kept?.method,
							targetClassId,
							whatHappened: text,
							whatWasWrong: whatWasWrong.trim() || undefined,
						},
						{
							onSuccess: () => {
								setWhatHappened("");
								setTargetClassId("");
								setWhatWasWrong("");
								setSuggestion(null);
							},
						},
					);
				}}
			>
				<Field
					htmlFor="reanalysis-what-happened"
					label="What actually happened"
				>
					<TextArea
						id="reanalysis-what-happened"
						onBlur={readWordsOnBlur}
						onChange={(e) => setWhatHappened(e.target.value)}
						placeholder="Two customers argued by the till. Nobody took anything."
						rows={3}
						value={whatHappened}
					/>
				</Field>
				<div className="flex flex-wrap items-end gap-3">
					<Button
						disabled={suggest.isPending || !text}
						onClick={readWords}
						type="button"
						variant="secondary"
					>
						{suggest.isPending ? "Reading…" : "Suggest class"}
					</Button>
					<Field htmlFor="reanalysis-class" label="What was it really?">
						<Select
							id="reanalysis-class"
							onChange={(e) => setTargetClassId(e.target.value)}
							value={targetClassId}
						>
							<option value="">Choose…</option>
							<option value={NONE_CLASS_ID}>{NOTHING_ALERTABLE}</option>
							<optgroup label="Alertable">
								{classes
									.filter((cls) => cls.alertable)
									.map((cls) => (
										<option key={cls.classId} value={cls.classId}>
											{cls.displayName}
										</option>
									))}
							</optgroup>
							<optgroup label="Not alertable">
								{classes
									.filter((cls) => !cls.alertable)
									.map((cls) => (
										<option key={cls.classId} value={cls.classId}>
											{cls.displayName}
										</option>
									))}
							</optgroup>
						</Select>
					</Field>
				</div>
				{kept && (
					<p className="text-secondary text-sm">
						Pre-filled from your words
						{kept.method === "alias"
							? ` (matched “${kept.matchedAlias ?? ""}”)`
							: " (chosen by the classifier)"}{" "}
						— change it if that's wrong.
					</p>
				)}
				{suggestion?.result.method === "none" && (
					<p className="text-secondary text-sm">
						Your words didn't match a class we have. Choose one above.
					</p>
				)}
				{suggest.error && (
					<p className="text-error-primary text-sm" role="alert">
						{apiErrorMessage(suggest.error)}
					</p>
				)}
				<Field
					htmlFor="reanalysis-what-was-wrong"
					label="What did the event get wrong? (optional, internal)"
				>
					<TextArea
						id="reanalysis-what-was-wrong"
						onChange={(e) => setWhatWasWrong(e.target.value)}
						placeholder="Called an argument shoplifting."
						rows={2}
						value={whatWasWrong}
					/>
				</Field>
				{target && (
					<p
						className="border-secondary text-primary rounded-md border p-3 text-sm"
						data-testid="reanalysis-case"
						role="status"
					>
						{describeReanalysis({ context, kind, target })}
					</p>
				)}
				{blocker && <p className="text-warning-primary text-sm">{blocker}</p>}
				<div>
					<Button disabled={!canSubmit} type="submit">
						{request.isPending ? "Submitting…" : "Submit"}
					</Button>
				</div>
				{request.error && (
					<p className="text-error-primary text-sm" role="alert">
						{apiErrorMessage(request.error)}
					</p>
				)}
			</form>
		</DetailPanel>
	);
};
